import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';

import { useMutation, useQuery } from '@apollo/client';
import EditRewardsModal from '../../../components/EditRewardsModal';

import { 
  CreateWfRewardTemplateMutation,
  CreateWfRewardTemplateMutationVariables,
  DeleteWfRewardTemplateMutation,
  DeleteWfRewardTemplateMutationVariables,
  GetAllWheelFortuneRewardConfigsQuery,
  ItemAvailability,
  LevelRewardInput,
  UpdateWfRewardTemplateMutation,
  UpdateWfRewardTemplateMutationVariables,
  WheelFortuneEventConfig,
  WheelFortuneRewardConfig,
  WheelFortuneRewardTemplate
} from '../../../../../../__gqltypes__';
import Chip from '../../../components/Chip';
import { CREATE_WF_REWARD_TEMPLATE, DELETE_WF_REWARD_TEMPLATE, GET_ALL_WF_REWARD_TEMPLATE, UPDATE_WF_REWARD_TEMPLATE } from '../graphql';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import EditValue from '../../../components/EditValue';

type WFRewards = WheelFortuneEventConfig['rewards'];
type Template = WheelFortuneRewardTemplate;

type Props = {
  rewards: WFRewards;
  onUpdateEvent: (rewards: WFRewards) => void;
};
function checkIfNumber(val: string): number | undefined {
  return Number.isNaN(+val) ? undefined : +val;
};

function formatChance(val: number): string {
  if (!Number.isFinite(val) || Number.isNaN(val)) {
    return '';
  }
  const [s, e] = val.toFixed(4).split('.');
  let t = e?.length ?? 1;
  for (let i = t - 1; i >= 0; --i) {
    if (e[i] === '0' && i === t - 1) {
      t = i;
    } else {
      break;
    }
  }
  if (t === 0) {
    return s;
  }
  return s.concat('.', t !== -1 ? e.slice(0, t) : e);
}

const defaultWeight = 10;
export const REQUIRED_NUMBER_OF_REWARDS = 10;

export function WheelFortuneRewardsData({rewards: rew, onUpdateEvent}: Props): JSX.Element {
  const none = 'None';

  const [rewards, setRewards] = useState<WFRewards>(rew);
  const [showEditReward, setShowEditReward] = useState(false);
  const [selectedReward, setSelectedReward] = useState<LevelRewardInput | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isTemplateEdit, setIsTemplateEdit] = useState(false);
  const [rewardIndex, setRewardIndex] = useState(0);
  const [totalWeight, setTotalWeight] = useState(1);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(none);
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateRewards, setTemplateRewards] = useState<WFRewards>([]);
  const [templateName, setTemplateName] = useState('');
  const [editTemplateId, setEditTemplateId] = useState<string>(none);
  const [totalWeightT, setTotalWeightT] = useState(1);

  const { data: templateData, loading: dataLoading, refetch} = useQuery<GetAllWheelFortuneRewardConfigsQuery>(
    GET_ALL_WF_REWARD_TEMPLATE);

  const onUpdateTemplateData = useCallback(() => {
      refetch();
  }, [refetch]);

  const [createWFRTemplate, {loading: createLoading}] = useMutation<
    CreateWfRewardTemplateMutation, 
    CreateWfRewardTemplateMutationVariables
  >(CREATE_WF_REWARD_TEMPLATE, {
    onCompleted: onUpdateTemplateData,
    onError: () => {},
  });

  const [updateWFRTemplate, {loading: updateLoading}] = useMutation<
    UpdateWfRewardTemplateMutation, 
    UpdateWfRewardTemplateMutationVariables
  >(UPDATE_WF_REWARD_TEMPLATE, {
    onCompleted: onUpdateTemplateData,
    onError: () => {},
  });

  const [deleteWFRTemplate, {loading: deleteLoading}] = useMutation<
    DeleteWfRewardTemplateMutation, 
    DeleteWfRewardTemplateMutationVariables
  >(DELETE_WF_REWARD_TEMPLATE, {
    onCompleted: onUpdateTemplateData,
  });

  useEffect(() => {
    setRewards(rew);
    setTotalWeight(rew.reduce((prev, cur) => prev + cur.weight, 0));
  }, [rew]);

  useEffect(() => {
    if (templateData?.liveOps?.getAllWheelFortuneRewardConfigs) {
      setTemplates(templateData.liveOps.getAllWheelFortuneRewardConfigs);
    }
  }, [templateData]);

  const handleChangeWeight = useCallback((newWeight: number, index: number, isTemplate = false) => {
    const r = isTemplate ? templateRewards : rewards;
    const newRewards = [...r];
    newRewards[index].weight = newWeight;
    if (isTemplate) {
      setTemplateRewards(newRewards);
      setTotalWeightT(newRewards.reduce((prev, cur) => prev + cur.weight, 0));
    } else {
      setRewards(newRewards);
      setTotalWeight(newRewards.reduce((prev, cur) => prev + cur.weight, 0));
    }
  }, [rewards, templateRewards]);

  const handleAddReward = useCallback((newReward: LevelRewardInput) => {
    const r = [...(isTemplateEdit ? templateRewards : rewards)];
    const rObj: WheelFortuneRewardConfig = {
        __typename: 'WheelFortuneRewardConfig',
        weight: defaultWeight,
        reward: {
          __typename: 'GenericLevelReward',
        ...newReward,
        }
      };
    if (isEditMode) {
      const val = r[rewardIndex];
      rObj.weight = val.weight;
      r[rewardIndex] = rObj;
    } else {
      r.push(rObj);
    }
    if (!isTemplateEdit) {
      onUpdateEvent(r);
    } else {
      setTemplateRewards(r);
      setTotalWeightT(r.reduce((prev, cur) => prev + cur.weight, 0));
    }
    setShowEditReward(false);
  }, [isEditMode, isTemplateEdit, onUpdateEvent, rewardIndex, rewards, templateRewards]);

  const handleRemoveReward = useCallback((idx: number, isTemplate = false) => {
    const r = isTemplate ? templateRewards : rewards;
    if (idx >= 0 && idx < r.length) {
      const newRewards = r.filter((val, index) => index !== idx);
      if (isTemplate) {
        setTemplateRewards(newRewards);
      } else {
        onUpdateEvent(newRewards);
      }
    }
  }, [onUpdateEvent, rewards, templateRewards]);

  const handleShowEditReward = useCallback((idx: number, isEdit: boolean, isTemplate = false) => {
    setIsEditMode(isEdit);
    if (isEdit) {
      const r = isTemplate ? templateRewards :rewards;
      const rew_ = r[idx].reward;
      setSelectedReward(rew_);
    } else {
      setSelectedReward(null);
    }
    setIsTemplateEdit(isTemplate);
    setRewardIndex(idx);
    setShowEditReward(true);
  }, [rewards, templateRewards]);

  const createTemplate = useCallback(() => {
    createWFRTemplate({
      variables: {
        input: {
          name: templateName,
          rewards: templateRewards.map((val) => {
            return {
              weight: val.weight,
              reward: {
                type: val.reward.type,
                amount: val.reward.amount,
                itemId: val.reward.itemId,
              }
            };
          }),
        }
      }
    });
  }, [createWFRTemplate, templateName, templateRewards]);

  const updateTemplate = useCallback(() => {
    updateWFRTemplate({
      variables: {
        input: {
          id: editTemplateId,
          name: templateName,
          rewards: templateRewards.map((val) => {
            return {
              weight: val.weight,
              reward: {
                type: val.reward.type,
                amount: val.reward.amount,
                itemId: val.reward.itemId,
              }
            };
          }),
        }
      }
    });
  }, [editTemplateId, templateName, templateRewards, updateWFRTemplate]);

  const deleteTemplate = useCallback(() => {
    deleteWFRTemplate({
      variables: {
        id: editTemplateId,
      }
    });
  }, [deleteWFRTemplate, editTemplateId]);

  const onSelectTemplate = useCallback((id: string, isTemplate = false) => {
    const tmp = templates.find((temp) => temp.id === id);
    if (tmp) {
      if (isTemplate) {
        setEditTemplateId(id);
        setTemplateName(tmp.name);
        setTemplateRewards(tmp.rewards);
        setTotalWeightT(tmp.rewards.reduce((prev, cur) => prev + cur.weight, 0));
      } else {
        setSelectedTemplateId(id);
        setRewards(tmp.rewards);
        onUpdateEvent(tmp.rewards);
        setTotalWeight(tmp.rewards.reduce((prev, cur) => prev + cur.weight, 0));
      }
    } else if (id === none) {
      if (isTemplate) {
        setTemplateName(none);
        setEditTemplateId(none);
        setTemplateRewards([]);
        setTotalWeightT(1);
      } else {
        setSelectedTemplateId(none);
        setRewards(rew);
        setTotalWeight(rew.reduce((prev, cur) => prev + cur.weight, 0));
      }
    }
  }, [onUpdateEvent, rew, templates]);

  const onClearRewards = useCallback((isTemplate = false) => {
    if (isTemplate) {
      setTemplateRewards([]);
    } else {
      setRewards([]);
    }
  }, []);

  const reqCount = REQUIRED_NUMBER_OF_REWARDS;
  const isEnoughRewards = reqCount === rewards.length;
  const isEnoughTRewards = reqCount === templateRewards.length;
  const templatesLoading = dataLoading || createLoading || updateLoading || deleteLoading;

  return (
    <>
      <EditRewardsModal
        isEdit={isEditMode}
        tier={null}
        show={showEditReward}
        level={0}
        reward={selectedReward}
        handleSave={handleAddReward}
        handleClose={() => setShowEditReward(false)}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        enableItemLevelEdit
        liveOpsAvailabilities={[ItemAvailability.EARN]}
      />

      <Card style={{width: '100%', marginBottom: 10}}>
        <Card.Header className="text-center">
          <Card.Title>Rewards</Card.Title>
          <Row>
            <Button 
              className="mr-2"
              variant='success'
              onClick={() => handleShowEditReward(rewards.length, false)}
              disabled={isEnoughRewards}>
              <i className='fas fa-plus'/> Add reward
            </Button>
            <Button 
              className="mr-2"
              variant='danger'
              onClick={() => onClearRewards()}>
              <i className='fas fa-trash'/> Clear Rewards
            </Button>
          </Row>
            {!isEnoughRewards && (
              <Card.Text className="text-danger">{REQUIRED_NUMBER_OF_REWARDS} rewards are required for Wheel Fortune event
              </Card.Text>
            )}
            <Row>
              <Col className="d-flex flex-column align-items-center">
                <Form.Label>Choose Rewards Template</Form.Label>
                  <Form.Control
                    as="select"
                    value={selectedTemplateId}
                    className="mb-4 w-25"
                    onChange={(e) => onSelectTemplate(e.target.value)}
                  >
                    <option key={none} value={none}>{none}</option>
                    {templates.map((temp) => {
                      return (
                        <option key={temp.id} value={temp.id}>
                          {temp.name}
                        </option>
                      );
                    })}
                  </Form.Control>
              </Col>
            </Row>
          </Card.Header>
        <Card.Body>
            <Table bordered responsive hover size="sm">
              <thead className="text-center">
                <tr>
                  <th>reward</th>
                  <th>weight</th>
                  <th>chance</th>
                </tr>
              </thead>
              <tbody>
                {rewards.map((val, index) => {
                  return (
                    <tr key={index} >
                      <td className="align-middle" >
                        <div className="d-flex justify-content-center align-items-center">
                          <Chip 
                            reward={val.reward} 
                            canDelete 
                            btnAction={() => handleRemoveReward(index)} 
                            globalAction={() => handleShowEditReward(index, true)}/>
                        </div>
                      </td>
                      <td >
                        <Col className="d-flex flex-column align-items-center">
                         <EditValue
                            name=""
                            value={val.weight}
                            handleValueChange={(e) => {
                            const newWeight = checkIfNumber(e) ?? 0;
                            handleChangeWeight(newWeight, index);
                          }}/>
                        </Col>
                      </td>
                      <td  style={{verticalAlign: 'middle'}} >
                        <Col className="d-flex flex-column align-items-center">
                          <Form.Label className="font-weight-bold w-75">
                              {formatChance((val.weight / totalWeight) * 100)}
                            </Form.Label>
                        </Col>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
        </Card.Body>
      </Card>

      <Card style={{width: '100%'}}>
        <Card.Header className="text-center">
          <Card.Title>Templates</Card.Title>
          <Row>
            <Button 
              className="mr-2"
              variant='success'
              onClick={() => handleShowEditReward(templateRewards.length, false, true)}
              disabled={isEnoughTRewards}>
              <i className='fas fa-plus'/> Add reward
            </Button>
            <Button className="mr-2" onClick={createTemplate} disabled={!isEnoughTRewards}>
              <i className='fas fa-plus'/> Create Template
            </Button>
            <Button className="mr-2" onClick={updateTemplate} disabled={editTemplateId === none}>
              <i className='fas fa-edit'/> Update Template
            </Button>
            <Button className="mr-2" variant="danger" onClick={deleteTemplate} disabled={editTemplateId === none}>
              <i className='fas fa-trash'/> Delete Template
            </Button>
            <Button 
              className="mr-2"
              variant='danger'
              onClick={() => onClearRewards(true)}>
              <i className='fas fa-trash'/> Clear Rewards
            </Button>
          </Row>
           {!isEnoughTRewards && (
              <Card.Text className="text-danger">{REQUIRED_NUMBER_OF_REWARDS} rewards are required for Wheel Fortune event
              </Card.Text>
            )}
          <Row>
            <Col className="d-flex flex-column align-items-center">
              <Form.Label className="w-50">Name</Form.Label>
              <Form.Control
                className="w-50"
                type="text"
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                }}
                />
            </Col>
            <Col className="d-flex flex-column align-items-center">
            <Form.Label>Choose Rewards Template</Form.Label>
              <Form.Control
                as="select"
                value={editTemplateId}
                className="mb-4 w-50"
                onChange={(e) => onSelectTemplate(e.target.value, true)}
              >
                <option key={none} value={none}>{none}</option>
                {templates.map((temp) => {
                  return (
                    <option key={temp.id} value={temp.id}>
                      {temp.name}
                    </option>
                  );
                })}
              </Form.Control>
              </Col>
          </Row>
        </Card.Header>
         <Card.Body>
          {templatesLoading ? (
            <LoadingLogo show />
          ) : (
            <Table bordered responsive hover size="sm">
              <thead className="text-center">
                <tr>
                  <th>reward</th>
                  <th>weight</th>
                  <th>chance</th>
                </tr>
              </thead>
              <tbody>
                {templateRewards.map((val, index) => {
                  return (
                    <tr key={index} >
                      <td className="align-middle" >
                        <div className="d-flex justify-content-center align-items-center">
                          <Chip 
                            reward={val.reward} 
                            canDelete 
                            btnAction={() => handleRemoveReward(index, true)} 
                            globalAction={() => handleShowEditReward(index, true, true)}/>
                        </div>
                      </td>
                      <td >
                        <Col className="d-flex flex-column align-items-center">
                            <EditValue
                              name=""
                              value={val.weight}
                              handleValueChange={(e) => {
                              const newWeight = checkIfNumber(e) ?? 0;
                              handleChangeWeight(newWeight, index, true);
                            }}
                            />
                        </Col>
                      </td>
                      <td  style={{verticalAlign: 'middle'}} >
                        <Col className="d-flex flex-column align-items-center">
                          <Form.Label className="font-weight-bold w-50">
                              {formatChance((val.weight / totalWeightT) * 100)}
                            </Form.Label>
                        </Col>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
}